/**
 * NcHeaderComponent — En-tête du site.
 * Gère : shadow scroll, menu mobile, modale contact, dropdowns clavier, recherche prédictive.
 */
if (!customElements.get('nc-header-component')) {
  class NcHeaderComponent extends NCComponent {
    #debounceTimer       = null;
    #cachedResults       = {};
    #activeIndex         = -1;
    #closeMobile         = null;
    #releaseMobileTrap   = null;
    #boundScroll         = null;
    #boundDocKeydown     = null;
    #boundDocClick       = null;
    #boundContactTrigger = null;
    #boundLangDoc        = null;

    connectedCallback() {
      this.#initScrollShadow();
      this.#initMobileMenu();
      this.#initContactModal();
      this.#initDropdownKeyboard();
      this.#initLangSelector();
      this.#initSearch();
    }

    disconnectedCallback() {
      super.disconnectedCallback();
      clearTimeout(this.#debounceTimer);
      if (this.#boundScroll)           window.removeEventListener('scroll',  this.#boundScroll);
      if (this.#boundDocKeydown)       document.removeEventListener('keydown', this.#boundDocKeydown);
      if (this.#boundDocClick)         document.removeEventListener('click',   this.#boundDocClick);
      if (this.#boundContactTrigger)   document.removeEventListener('click',   this.#boundContactTrigger);
      if (this.#boundLangDoc)          document.removeEventListener('click',   this.#boundLangDoc);
      if (this.#boundLangDoc)          document.removeEventListener('keydown', this.#boundLangDoc);
    }

    // Sélecteur de langue : ouverture/fermeture accessible (clic, clic-dehors, Échap).
    #initLangSelector() {
      const selectors = this.$$('[data-nc-langsel]');
      if (!selectors.length) return;

      const closeAll = (except) => {
        selectors.forEach((sel) => {
          if (sel === except) return;
          const trigger = sel.querySelector('[data-nc-langsel-trigger]');
          const menu = sel.querySelector('[data-nc-langsel-menu]');
          sel.classList.remove('is-open');
          trigger?.setAttribute('aria-expanded', 'false');
          if (menu) menu.hidden = true;
        });
      };

      selectors.forEach((sel) => {
        const trigger = sel.querySelector('[data-nc-langsel-trigger]');
        const menu = sel.querySelector('[data-nc-langsel-menu]');
        if (!trigger || !menu) return;

        trigger.addEventListener('click', (e) => {
          e.stopPropagation();
          const willOpen = !sel.classList.contains('is-open');
          closeAll(sel);
          sel.classList.toggle('is-open', willOpen);
          trigger.setAttribute('aria-expanded', String(willOpen));
          menu.hidden = !willOpen;
          if (willOpen) {
            const first = menu.querySelector('.nc-langsel__item');
            requestAnimationFrame(() => first?.focus());
          }
        });
      });

      this.#boundLangDoc = (e) => {
        if (e.type === 'keydown') {
          if (e.key === 'Escape') {
            const open = [...selectors].find((s) => s.classList.contains('is-open'));
            if (open) {
              closeAll(null);
              open.querySelector('[data-nc-langsel-trigger]')?.focus();
            }
          }
          return;
        }
        // click en dehors d'un sélecteur ouvert
        if (![...selectors].some((s) => s.contains(e.target))) closeAll(null);
      };
      document.addEventListener('click', this.#boundLangDoc);
      document.addEventListener('keydown', this.#boundLangDoc);
    }

    #initScrollShadow() {
      const nav = this.$('#nc-hdr-nav');
      if (!nav) return;
      this.#boundScroll = () => nav.classList.toggle('is-scrolled', window.scrollY > 10);
      window.addEventListener('scroll', this.#boundScroll, { passive: true });
    }

    #initMobileMenu() {
      const hamburger  = this.$('#nc-hdr-hamburger');
      const mobileMenu = this.$('#nc-hdr-mobile');
      const closeBtn   = this.$('#nc-hdr-mobile-close');

      const openMobile = () => {
        if (!mobileMenu) return;
        mobileMenu.classList.add('is-open');
        mobileMenu.removeAttribute('inert');
        mobileMenu.setAttribute('aria-modal', 'true');
        hamburger?.setAttribute('aria-expanded', 'true');
        document.body.style.overflow = 'hidden';
        this.#releaseMobileTrap = window.NCA11y?.trapFocus(mobileMenu, { onEscape: this.#closeMobile });
        window.requestAnimationFrame(() => window.NCA11y?.focusFirst(mobileMenu, '#nc-hdr-mobile-close'));
      };

      this.#closeMobile = () => {
        if (!mobileMenu) return;
        this.#releaseMobileTrap?.();
        this.#releaseMobileTrap = null;
        mobileMenu.classList.remove('is-open');
        mobileMenu.setAttribute('inert', '');
        mobileMenu.removeAttribute('aria-modal');
        hamburger?.setAttribute('aria-expanded', 'false');
        document.body.style.overflow = '';
        hamburger?.focus();
      };

      hamburger?.addEventListener('click', openMobile);
      closeBtn?.addEventListener('click',  this.#closeMobile);

      this.#boundDocKeydown = e => {
        if (e.key === 'Escape' && mobileMenu?.classList.contains('is-open')) this.#closeMobile?.();
      };
      document.addEventListener('keydown', this.#boundDocKeydown);
    }

    #initContactModal() {
      const contactModal      = this.$('#nc-contact-modal');
      const contactOpen       = this.$('#nc-contact-open');
      const contactOpenMobile = this.$('.nc-contact-open-mobile');
      const contactClose      = contactModal?.querySelector('.nc-cmodal__close');
      const hamburger         = this.$('#nc-hdr-hamburger');

      const openContactModal = trigger => {
        if (!contactModal) return;
        const returnTarget = trigger?.closest?.('#nc-hdr-mobile') ? hamburger : trigger;
        this.#closeMobile?.();
        window.NCA11y?.openDialog(contactModal, { trigger: returnTarget, initialFocus: '.nc-cmodal__close' });
      };

      contactOpen?.addEventListener('click', e => openContactModal(e.currentTarget));
      contactOpenMobile?.addEventListener('click', e => openContactModal(e.currentTarget));
      contactClose?.addEventListener('click', () => window.NCA11y?.closeDialog(contactModal));
      contactModal?.addEventListener('click', e => { if (e.target === contactModal) window.NCA11y?.closeDialog(contactModal); });
      contactModal?.addEventListener('cancel', e => {
        e.preventDefault();
        window.NCA11y?.closeDialog(contactModal);
      });

      // Délégation globale pour tous les boutons .nc-contact-trigger de la page
      this.#boundContactTrigger = e => {
        const trigger = e.target.closest('.nc-contact-trigger');
        if (trigger) { e.preventDefault(); openContactModal(trigger); }
      };
      document.addEventListener('click', this.#boundContactTrigger);
    }

    #initDropdownKeyboard() {
      const links = this.$$('.nc-hdr__menu-link[aria-haspopup]');
      const closeItem = link => link.setAttribute('aria-expanded', 'false');
      const openItem = link => {
        links.forEach(other => { if (other !== link) closeItem(other); });
        link.setAttribute('aria-expanded', 'true');
      };

      links.forEach(link => {
        const item = link.closest('.nc-hdr__menu-item');
        if (!item) return;

        item.addEventListener('mouseenter', () => openItem(link));
        item.addEventListener('mouseleave', () => closeItem(link));
        item.addEventListener('focusin', () => openItem(link));
        item.addEventListener('focusout', e => {
          if (!item.contains(e.relatedTarget)) closeItem(link);
        });

        link.addEventListener('keydown', e => {
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            openItem(link);
            item.querySelector('.nc-hdr__dropdown-link')?.focus();
          }
          if (e.key === 'Escape') { closeItem(link); link.focus(); }
        });

        item.querySelectorAll('.nc-hdr__dropdown-link').forEach(dropdownLink => {
          dropdownLink.addEventListener('keydown', e => {
            if (e.key === 'Escape') {
              e.preventDefault();
              closeItem(link);
              link.focus();
            }
          });
        });
      });
    }

    #initSearch() {
      const searchInput  = this.$('#nc-hdr-search-input');
      const dropdown     = this.$('#nc-hdr-search-dropdown');
      const popularPanel = this.$('#nc-hdr-dd-popular');
      const resultsPanel = this.$('#nc-hdr-dd-results');
      const searchForm   = this.$('#nc-hdr-search-form');

      if (!searchInput || !dropdown) return;

      const openDropdown  = () => { dropdown.classList.add('is-open');    searchInput.setAttribute('aria-expanded', 'true'); };
      const closeDropdown = () => { dropdown.classList.remove('is-open'); searchInput.setAttribute('aria-expanded', 'false'); this.#activeIndex = -1; clearFocus(); };
      const showPopular   = () => { popularPanel.style.display = 'block'; resultsPanel.style.display = 'none';  this.#activeIndex = -1; clearFocus(); };
      const showResults   = html => { popularPanel.style.display = 'none'; resultsPanel.style.display = 'block'; resultsPanel.innerHTML = html; this.#activeIndex = -1; clearFocus(); };

      const clearFocus = () => dropdown.querySelectorAll('.nc-hdr__dd-item[aria-selected="true"]').forEach(el => el.setAttribute('aria-selected', 'false'));
      const setFocus   = idx => {
        clearFocus();
        const items = this.#getItems(resultsPanel, popularPanel);
        if (idx >= 0 && idx < items.length) { items[idx].setAttribute('aria-selected', 'true'); items[idx].scrollIntoView({ block: 'nearest' }); }
      };

      const escHtml = s => { if (!s) return ''; const d = document.createElement('div'); d.textContent = s; return d.innerHTML; };

      const buildRow = p => {
        let img;
        if (p.featured_image?.url) {
          img = `<img src="${p.featured_image.url.replace(/(\?|&)width=\d+/, '')}?width=120" alt="${escHtml(p.featured_image.alt || p.title)}" width="52" height="52" loading="lazy">`;
        } else if (p.image) {
          img = `<img src="${p.image}?width=120" alt="${escHtml(p.title)}" width="52" height="52" loading="lazy">`;
        } else {
          img = `<div class="nc-hdr__dd-placeholder"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" width="24" height="24"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"/></svg></div>`;
        }
        const details = p.product_type ? escHtml(p.product_type) : '';
        return `<li class="nc-hdr__dd-item" role="option" aria-selected="false"><a href="${p.url}" class="nc-hdr__dd-row" aria-label="${escHtml(p.title)}"><div class="nc-hdr__dd-thumb">${img}</div><div class="nc-hdr__dd-info"><span class="nc-hdr__dd-name">${escHtml(p.title)}</span>${details ? `<span class="nc-hdr__dd-details">${details}</span>` : ''}</div></a></li>`;
      };

      const renderResults = (data, query) => {
        const products = data.resources?.results?.products || [];
        if (!products.length) { showResults(`<div class="nc-empty-state nc-empty-state--compact">Aucun r\u00e9sultat pour \u00ab ${escHtml(query)} \u00bb</div>`); return; }
        let html = `<h4 class="nc-hdr__dd-title"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="14" height="14"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>R\u00e9sultats</h4><ul class="nc-hdr__dd-list" role="group">`;
        products.forEach(p => { html += buildRow(p); });
        html += `</ul><a href="${window.location.origin}/search?type=product&q=${encodeURIComponent(query)}" class="nc-hdr__dd-viewall">Voir tous les r\u00e9sultats \u2192</a>`;
        showResults(html);
        openDropdown();
      };

      const fetchResults = async query => {
        const key = query.toLowerCase().trim();
        if (this.#cachedResults[key]) { renderResults(this.#cachedResults[key], query); return; }

        showResults('<div class="nc-hdr__dd-spinner"></div>');
        openDropdown();

        const url = '/search/suggest.json?q=' + encodeURIComponent(query) + '&resources[type]=product&resources[limit]=8&resources[options][unavailable_products]=last';
        const res = await this.fetchWith(url);
        if (!res) return; // requ\u00eate annul\u00e9e

        const data = await res.json();
        this.#cachedResults[key] = data;
        renderResults(data, query);
      };

      searchInput.addEventListener('focus', () => {
        if (!searchInput.value.trim()) showPopular();
        openDropdown();
      });

      searchInput.addEventListener('input', () => {
        const q = searchInput.value.trim();
        clearTimeout(this.#debounceTimer);
        if (!q) { this.abort(); showPopular(); openDropdown(); return; }
        this.#debounceTimer = setTimeout(() => fetchResults(q), 200);
      });

      searchInput.addEventListener('keydown', e => {
        if (!dropdown.classList.contains('is-open')) return;
        const items = this.#getItems(resultsPanel, popularPanel);
        if (!items.length) return;

        if (e.key === 'ArrowDown')      { e.preventDefault(); this.#activeIndex = (this.#activeIndex + 1) % items.length; setFocus(this.#activeIndex); }
        else if (e.key === 'ArrowUp')   { e.preventDefault(); this.#activeIndex = this.#activeIndex <= 0 ? items.length - 1 : this.#activeIndex - 1; setFocus(this.#activeIndex); }
        else if (e.key === 'Enter' && this.#activeIndex >= 0) { e.preventDefault(); items[this.#activeIndex].querySelector('a')?.click(); }
        else if (e.key === 'Escape')    { closeDropdown(); searchInput.blur(); }
      });

      this.#boundDocClick = e => { if (!searchForm.contains(e.target)) closeDropdown(); };
      document.addEventListener('click', this.#boundDocClick);

      searchForm.addEventListener('submit', e => {
        if (this.#activeIndex >= 0) {
          e.preventDefault();
          const items = this.#getItems(resultsPanel, popularPanel);
          items[this.#activeIndex]?.querySelector('a')?.click();
        }
      });
    }

    #getItems(resultsPanel, popularPanel) {
      const panel = resultsPanel.style.display !== 'none' ? resultsPanel : popularPanel;
      return [...panel.querySelectorAll('.nc-hdr__dd-item')];
    }
  }

  customElements.define('nc-header-component', NcHeaderComponent);
}
